<template>

   <!-- XOL-371 review 2026-07-21: request creation reverted to January — a Tower Co user must be a project/finance/admin manager; non-Tower-Co orgs unaffected. API gates POST /actions to match. -->
   <!-- XOL-393 (Carlos, 2026-07-27): a view-only user does not see the create button at
        all. Gated HERE rather than per-caller because RequestAction owns the affordance
        and renders on three surfaces (RequestTable, SitesTable, HomeMap).

        ⚠ CURRENTLY UNREACHABLE (verified 2026-07-28). The manager/approver/viewer flags
        are a TOWER COMPANY concept; no other org category carries them. SitesTable and
        HomeMap render this under isTelco()/isBackhaulCo(), whose users never have
        `viewer`; and RequestTable only reaches it when canCreateRequest() is true, which
        for a Tower Co viewer additionally requires isManager() — and no contact holds
        both flags. Kept as defence-in-depth: it goes live the moment someone is given
        manager AND viewer together. Do not cite it as live behaviour.
        See docs/UX-architecture/access/edit-access-register.md — XOL-393 section. -->
   <popup
    v-if="!isViewOnly()"
    ref="popup"
    :button-style="scheme.colour"
    :heading="'Request to ' + scheme.text"
    :show-text="showText"
    :size="this.adding ? 'small' : 'default'"
    :button-type="buttonType"
    :button-text="scheme.text"
    :center-button="true"
    :round="round"
    :show-save="adding"
    :bg-class="formatBG"
    :button-icon="scheme.icon"
    :disableButton="(isTowerCo() && !isManager('project|finance|admin')) || isViewOnly() || rtbBlocked"
    :keep-open-until-saved="addingRta"
    :before-save="addingRta ? validateRta : undefined"
    @save="onSaveDialog"
    @close="onCloseDialog">

    <section v-if="type === 'RTA'">
        <el-row>
            <el-col :span="24">

              <acquire-request
                v-if="adding"
                ref="ad"
                :client-id="this.orgId"
                :table-ref="tref"
                @saved="onRtaSaved"
                @save-failed="onRtaSaveFailed" />

              <!-- XOL-67 (B3): an existing RTA is the RtaDetails drawer, read from the action. -->
              <rta-details v-else ref="ad" :row-data="rowData" />

            </el-col>
        </el-row>
      </section>

      <section v-else>

        <action-details
          v-if="adding"
          ref="ad"
          :site-id="siteId"
          :row-data="rowData"
          :editMode="true"
          :type="type"
          :adding="true"
          :hide-panel="true"
          :table-ref="tref" />

        <request-details v-else :row-data="rowData" />

      </section>

   </popup>

</template>


<script>

import AcquireRequest from '@/components/requests/acquire/AcquireRequest'

import RtaDetails from '@/components/requests/acquire/RtaDetails'
import ActionDetails from '@/components/actions/ActionDetails'

import RequestDetails from '@/components/requests/RequestDetails'

export default {

  name: 'RequestAction',

  components: {
    AcquireRequest,
    RtaDetails,
    ActionDetails,
    RequestDetails
  },

  props: {

    showText: {
      type: Boolean,
      default: true
    },

    type: {
      type: String,
      default: 'RTS'
    },

    siteId: {
      type: Number,
      default: null
    },

    orgId: {
      type: Number,
      default: null
    },

    rowData: {
      type: Object,
      default: null
    },

    adding: {
      type: Boolean,
      default: true
    },

    buttonType: {
      type: String,
      default: null
    },

    round: {
      type: Boolean,
      default: false
    },

    tableRef: {
      type: String,
      default: '-action-table'
    }
  },



  computed: {
    // XOL-675: only the Acquire create form answers the Popup's saveSucceeded()/saveFailed()
    // handshake (keep-open-until-saved), so only it opts in. Every other request keeps today's
    // close-on-save.
    addingRta: function () {
      return this.type === 'RTA' && this.adding
    },

    formatBG: function() {
      let cls = null

      switch (this.type) {
        case 'RTA':
          cls = 'bg-warning-lighten-2'
          break

        case 'RTS':
          cls = 'bg-success-lighten-2'
          break

        case 'RTU':
          cls = 'bg-primary-lighten-1'
          break

        case 'RTB':
          cls = 'bg-info-lighten-2'
          break

        case 'RTD':
        cls =  'bg-danger-ligthen-2'
        break

      }

      return cls
    },

    rtbBlocked: function () {
      // Telco-only gate: orgs with canCreateRTB explicitly false cannot create
      // an RTB. Undefined is treated as enabled (tolerant of stale session
      // cache before the API column ships). Non-Telco users are unaffected.
      const o = this.org()
      return this.type === 'RTB' && this.isTelco() && o && o.canCreateRTB === false
    }

  },


  methods: {

    onSaveDialog () {

      if (this.$refs.ad && typeof this.$refs.ad.onSave === 'function') {
        this.$refs.ad.onSave()
      }

    },

    onCloseDialog () {

      if (this.$refs.ad && typeof this.$refs.ad.onClose === 'function') {
        this.$refs.ad.onClose()
      }
    },

    // :before-save gate (RTA create): false keeps the drawer open with the field errors shown.
    validateRta () {
      return this.$refs.ad && this.$refs.ad.validateForm ? this.$refs.ad.validateForm() : true
    },

    // The server answered the whole create: close the drawer (AcquireRequest has already
    // refreshed the RTA list, after the POST resolved).
    onRtaSaved () {
      if (this.$refs.popup) {
        this.$refs.popup.saveSucceeded()
      }
    },

    // A refusal: the drawer stays open with every field as typed.
    onRtaSaveFailed () {
      if (this.$refs.popup) {
        this.$refs.popup.saveFailed()
      }
    }

  },

  data () {
    return {
      tref: this.type.toLowerCase() + this.tableRef,
      scheme: {
        text: 'Share',
        icon: 'fa fa-share-alt',
        colour: 'success'
      },
    }
  },

  created () {

    if (this.type === 'RTA') {
      this.scheme.text = 'Acquire'
      this.scheme.icon = 'fa fa-street-view'
      this.scheme.colour = 'warning'
    }

    if (this.type === 'RTU') {
      this.scheme.text = 'Upgrade'
      this.scheme.icon = 'fa fa-tools'
      this.scheme.colour = 'primary'
    }

    if (this.type === 'RTB') {
      this.scheme.text = 'Backhaul'
      this.scheme.icon = 'fa fa-wifi'
      this.scheme.colour = 'info'
    }

    if (this.type === 'RTD') {
      this.scheme.text = 'Dismantle'
      this.scheme.icon = 'fa fa-xmark'
      this.scheme.colour = 'danger'
    }



  }
}

</script>
